import React, { useEffect, useRef, useState } from "react";
import { Cookie, Sparkles, X } from "lucide-react";
import { Link } from "react-router-dom";

/**
 * Cookie banner — implements exact spec:
 * - fixed bottom-right (glass)
 * - slide-up entrance after 800ms delay
 * - 3D flip on button press (600ms), 180ms pause, then slide-right exit (470ms)
 * - localStorage key: cookie_choice = 'accepted' | 'necessary'
 */
export default function CookieBanner() {
    const [visible, setVisible] = useState(false);
    const [exiting, setExiting] = useState(false);
    const [flippedKey, setFlippedKey] = useState(null); // 'accepted' | 'necessary'
    const [locked, setLocked] = useState(false);
    const timers = useRef([]);

    useEffect(() => {
        const saved = localStorage.getItem("cookie_choice");
        if (saved) return;
        const t = setTimeout(() => setVisible(true), 800);
        timers.current.push(t);
        return () => timers.current.forEach(clearTimeout);
    }, []);

    const handleChoice = (choice) => {
        if (locked) return;
        setLocked(true);
        setFlippedKey(choice);
        const t1 = setTimeout(() => {
            try {
                localStorage.setItem("cookie_choice", choice);
            } catch (e) {
                /* storage not available */
            }
            const t2 = setTimeout(() => {
                setExiting(true);
                const t3 = setTimeout(() => {
                    setVisible(false);
                }, 470);
                timers.current.push(t3);
            }, 180);
            timers.current.push(t2);
        }, 600);
        timers.current.push(t1);
    };

    if (!visible) return null;

    return (
        <div
            data-testid="cookie-banner"
            className={`fixed z-[60] bottom-4 right-4 left-4 md:left-auto md:bottom-6 md:right-6 md:max-w-md ${
                exiting ? "is-exiting-right" : "cookie-enter"
            }`}
        >
            <div className="relative overflow-hidden rounded-3xl border border-ivory/60 bg-ivory/85 backdrop-blur-2xl p-6 md:p-7 shadow-soft-lg">
                <div
                    aria-hidden
                    className="pointer-events-none absolute -top-16 -right-16 w-56 h-56 rounded-full bg-raspberry/40 blur-3xl opacity-50"
                />
                <div
                    aria-hidden
                    className="pointer-events-none absolute -top-10 right-10 w-40 h-40 rounded-full bg-sage/50 blur-3xl opacity-40"
                />

                <button
                    data-testid="cookie-banner-close"
                    aria-label="Chiudi"
                    onClick={() => handleChoice("necessary")}
                    className="absolute top-4 right-4 z-10 w-8 h-8 rounded-full grid place-items-center text-ink-soft/70 hover:text-ink hover:bg-ivory transition-colors"
                >
                    <X size={16} />
                </button>

                <div className="relative flex items-start gap-4">
                    <div
                        className="w-11 h-11 rounded-2xl grid place-items-center text-ivory shrink-0"
                        style={{
                            background:
                                "linear-gradient(135deg, hsl(340 55% 45%) 0%, hsl(14 65% 68%) 100%)",
                        }}
                    >
                        <Cookie size={20} />
                    </div>
                    <div className="pr-6">
                        <h3 className="font-display text-lg uppercase tracking-[0.15em] text-ink">
                            Cookie & privacy
                        </h3>
                        <p className="mt-2 text-[13.5px] text-ink-soft leading-relaxed">
                            Usiamo cookie tecnici per il funzionamento del sito.
                            Nessun tracker invasivo.{" "}
                            <Link
                                to="/cookie"
                                data-testid="cookie-banner-link"
                                className="text-raspberry hover:underline font-medium"
                            >
                                Scopri di più
                            </Link>
                        </p>
                    </div>
                </div>

                <div className="relative mt-6 flex flex-col md:flex-row gap-3">
                    <FlipButton
                        testId="cookie-banner-necessary"
                        onClick={() => handleChoice("necessary")}
                        flipped={flippedKey === "necessary"}
                        disabled={locked}
                        variant="ghost"
                    >
                        Solo necessari
                    </FlipButton>
                    <FlipButton
                        testId="cookie-banner-accept"
                        onClick={() => handleChoice("accepted")}
                        flipped={flippedKey === "accepted"}
                        disabled={locked}
                        variant="solid"
                        icon={<Sparkles size={14} className="ml-0.5" />}
                    >
                        Accetta tutto
                    </FlipButton>
                </div>
            </div>
        </div>
    );
}

function FlipButton({
    children,
    testId,
    onClick,
    flipped,
    disabled,
    variant,
    icon,
}) {
    const face =
        variant === "solid"
            ? "bg-ink text-ivory hover:bg-raspberry"
            : "bg-transparent border border-ink/15 text-ink hover:bg-ivory-warm";
    return (
        <div
            className={`cookie-flip flex-1 ${flipped ? "is-flipped" : ""}`}
        >
            <div className="cookie-flip-inner relative h-11">
                <button
                    data-testid={testId}
                    disabled={disabled}
                    onClick={onClick}
                    className={`cookie-flip-face absolute inset-0 rounded-full inline-flex items-center justify-center gap-2 px-5 text-[11px] uppercase font-bold tracking-[0.22em] transition-colors ${face} disabled:opacity-100 disabled:cursor-default`}
                >
                    {children}
                    {icon}
                </button>
                <div
                    className="cookie-flip-back rounded-full text-[11px] uppercase font-bold tracking-[0.22em]"
                    style={{
                        background:
                            "linear-gradient(135deg, hsl(140 55% 45%), hsl(120 50% 55%))",
                        color: "hsl(0 0% 100%)",
                    }}
                >
                    ✓ Confermato
                </div>
            </div>
        </div>
    );
}
